import { Box, Button, CircularProgress, Divider, IconButton, TextField, Toolbar, Typography, withStyles, WithStyles } from "@material-ui/core";
import { StarBorder, DirectionsBike, DirectionsWalk, Accessible, LocationOn, MyLocation, Save, Search, Clear, Check } from "@material-ui/icons";
import Autocomplete, { AutocompleteChangeReason, AutocompleteInputChangeReason } from "@material-ui/lab/Autocomplete";
import { LatLng, LatLngBounds, LeafletMouseEvent } from "leaflet";
import Geocode from "react-geocode";
import * as PolyUtil from "polyline-encoded";
import React, { ChangeEvent } from "react";
import { Map, Marker, Popup, TileLayer, Viewport, WMSTileLayer } from "react-leaflet";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { setDisplayedRoute } from "../../../actions/route";
import { setDisplayedFavouriteLocation } from "../../../actions/location";
import Api from "../../../api";
import { AirQuality, Route, FavouriteLocation, DefaultRoutingMode, Pollutant, PollutionEntry, RoutePollutionExposureRecord, TransportationType, RouteHistoryType } from "../../../generated/client";
import strings from "../../../localization/strings";
import { ReduxActions, ReduxState } from "../../../store";
import theme from "../../../theme/theme";
import { Location, NullableToken, GeocodeCoordinate, RouteTotalAirQuality, PointCoordinates, RouteData, RoutingModeData, HeaderNavigationLinks } from "../../../types";
import AppLayout from "../../layouts/app-layout/app-layout";
import SavedRoutes from "../../routes/saved-routes/saved-routes";
import FavouriteLocations from "../../favourite-locations/favourite-locations";
import PollutantSelection from "../../pollutant-selection/pollutant-selection";
import styles from "./map-screen.styles";
import ConfirmDialog from "../../generic/dialogs/confirm-dialog";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { KeyboardDatePicker, KeyboardTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { NUMBER_OF_RESULTS_FOR_FAVOURITE_PLACES } from "../../../constants/map";
import MapControl from "../../map-control/map-control";
import { sourceIcon } from "../../../resources/images/svg/map-marker-source";
import { destinationIcon } from "../../../resources/images/svg/map-marker-destination";
import AirQualitySlider from "../../pollutant-exposures/air-quality-slider";
import RoutingUtils from "../../../utils/routing-utils";
import RoutingModeIconRenderer from "../../generic/routing-mode-icon/routing-mode-icon-renderer";
import PollutantUtils from "../../../utils/pollutant-utils";
import ColoredRoute from "../../generic/colored-route/colored-route";
import classNames from "classnames";
import MapUtils from "../../../utils/map-utils";

/**
 * Interface describing component props
 */
interface Props extends WithStyles<typeof styles>{
  accessToken?: NullableToken;
  keycloak?: Keycloak.KeycloakInstance;
  displayedRoute?: Route;
  displayedFavouriteLocation?: FavouriteLocation;
  setDisplayedRoute: typeof setDisplayedRoute;
  setDisplayedFavouriteLocation: typeof setDisplayedFavouriteLocation;
}

/**
 * Interface describing component state
 */
interface State {
  locationFrom?: Location;
  locationTo?: Location;
  duplicateLocation?: FavouriteLocation;
  departureDate: Date;
  departureTime: Date;
  route?: RouteData;
  routeAlts: RoutingModeData[];
  selectedRoutingMode?: DefaultRoutingMode;
  mapViewport: Viewport;
  mapBounds?: LatLngBounds;
  editingLocationFrom: boolean;
  loadingRoute: boolean;
  locationFromOptions: Location[];
  locationToOptions: Location[];
  polyline?: string;
  savingRoute: boolean;
  savingLocation: boolean;
  mapAirQuality: AirQuality[];
  locationFromTextInput: string;
  locationToTextInput: string;
  loadingUserSettings: boolean;
  userSavedRoutes: Route[];
  userFavouriteLocations: FavouriteLocation[];
  userDialogInput?: string;
  error?: any;
  savingLocationCoordinates?: LatLng;
  mapInteractive: boolean;
  selectedFavouriteLocation?: Location;
  pollutantControlMapCenter: [number, number];
  WMSLayerVisible: boolean;
  displayAirQualitySection: boolean;
  loadingRouteAirQuality: boolean;
  pollutants: Pollutant[];
  defaultRoutingModes: DefaultRoutingMode[];
  selectedPollutant?: Pollutant;
}

/**
 * Map screen component
 */
class MapScreen extends React.Component<Props, State> {

  /**
   * Maps favourite locations to locations
   *
   * @param favouriteLocations  users favourite locations
   */
  static mapLocationsFromFavouriteLocations = (favouriteLocations: FavouriteLocation[]) => {
    return favouriteLocations.map(element => {
      const { name } = element;
      const coordinates = new LatLng(element.latitude, element.longitude);
      return { name: name, coordinates: coordinates } as Location;
    }).slice(
      0,
      NUMBER_OF_RESULTS_FOR_FAVOURITE_PLACES
    );
  };

  private COORDINATE_DELTA = 0.00001;
  mapRef: React.RefObject<Map>;
  overlayRef: any;
  sourceMarkerRef: React.RefObject<Marker>;
  /**
   * Component constructor
   *
   * @param props props
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      routeAlts: [],
      departureDate: new Date(),
      departureTime: new Date(),
      mapViewport: {
        zoom: 12,
        center: [
          parseFloat(process.env.REACT_APP_STARTING_COORDINATES_LAT || "60.1699"),
          parseFloat(process.env.REACT_APP_STARTING_COORDINATES_LNG || "24.9384")
        ]
      },
      pollutantControlMapCenter: [
        parseFloat(process.env.REACT_APP_STARTING_COORDINATES_LAT || "60.1699"),
        parseFloat(process.env.REACT_APP_STARTING_COORDINATES_LNG || "24.9384")
      ],
      editingLocationFrom: true,
      loadingRoute: false,
      locationFromOptions: [],
      locationToOptions: [],
      savingRoute: false,
      savingLocation: false,
      mapAirQuality: [],
      locationFromTextInput: "",
      locationToTextInput: "",
      loadingUserSettings: false,
      userSavedRoutes: [],
      pollutants: [],
      defaultRoutingModes: [],
      userFavouriteLocations: [],
      mapInteractive: true,
      WMSLayerVisible: false,
      displayAirQualitySection: false,
      loadingRouteAirQuality: false
    };

    this.mapRef = React.createRef();
    this.overlayRef = React.createRef();
    this.sourceMarkerRef = React.createRef();
  }

  /**
   * Component did mount life cycle method
   */
  public componentDidMount = async () => {
    const { displayedFavouriteLocation, displayedRoute, accessToken } = this.props;

    await this.getPollutants();
    this.getAirQualityPollutantData();
    await this.getDefaultRoutingModes();

    if (!accessToken) {
      return;
    }

    if (displayedRoute) {
      this.displaySavedRoute(displayedRoute);
    } else {
      this.loadUserSettings();
    }

    if (displayedFavouriteLocation) {
      this.displayFavouriteLocation(displayedFavouriteLocation);
    } else {
      this.loadUserSettings();
    }

    this.getUserSavedRoutes();
    this.getUserFavouriteLocations().then(() => this.setLocationOptions());
  };

  /**
   * Prepares the data for the WMS layer
   */
  private getAirQualityPollutantData = async () => {
    const { accessToken } = this.props;
    const {
      mapViewport,
      departureTime
    } = this.state;

    let mapBounds = this.mapRef.current?.leafletElement.getBounds();
    if (mapBounds === undefined) {
      mapBounds = PollutantUtils.createDefaultBounds(mapViewport);
    }
    const query = PollutantUtils.getAirQualityQuery(mapBounds, departureTime);

    try {
      Api.getAirQualityApi(accessToken).getAirQuality(query)
        .then(airQuality => {
          this.setState({
            mapAirQuality: airQuality
          });
        });
    } catch (error) {
      this.setState({
        error: error
      });
    }
  };

  /**
   * Map screen render method
   */
  public render = () => {
    const { accessToken, keycloak, classes } = this.props;
    const { loadingUserSettings, error } = this.state;

    if (loadingUserSettings) {
      return (
        <AppLayout
          currentHeaderNavigationLink={ HeaderNavigationLinks.map }
          showDrawer={ true }
          drawerContent={
            <div className={ classes.loader }>
              <CircularProgress size={ 64 }/>
            </div>
          }
        >
          <div className={ classes.loader }>
            <CircularProgress size={ 64 }/>
          </div>
        </AppLayout>
      );
    }

    return (
      <AppLayout
        currentHeaderNavigationLink={ HeaderNavigationLinks.map }
        accessToken={ accessToken }
        keycloak={ keycloak }
        showDrawer={ true }
        drawerContent={
          this.renderDrawerContent()
        }
        error={ error }
        clearError={ this.onClearError }
      >
        { this.renderMap() }
        { this.renderSaveRouteConfirmDialog() }
        { this.renderSaveLocationConfirmDialog() }
        { this.renderDuplicateLocationConfirmDialog() }
      </AppLayout>

    );
  };

  /**
   * Render drawer content
   */
  private renderDrawerContent = () => {
    const { accessToken, classes } = this.props;
    const {
      userSavedRoutes,
      userFavouriteLocations,
      displayAirQualitySection,
      loadingRouteAirQuality,
      pollutants,
      selectedPollutant,
      WMSLayerVisible,
      selectedRoutingMode,
      route,
      routeAlts
    } = this.state;

    // TODO fix exposure to show general ari quality value
    const selectedRouteAlt = routeAlts.find(routeAlt => routeAlt.name === selectedRoutingMode?.name);
    const displayedExposures = selectedRouteAlt?.associatedRouteData?.totalExposures || route?.totalExposures;

    return (
      <>
        {/* //TODO: make transportation selector similar to routing mode selector */}
        <Toolbar/>
        <Toolbar className={ classes.toolbar }>
          <Box style={{ display: "flex" }}>
            <IconButton>
              <DirectionsWalk htmlColor="#fff"/>
            </IconButton>
            <IconButton>
              <Accessible htmlColor="#fff"/>
            </IconButton>
            <IconButton>
              <DirectionsBike htmlColor="#fff"/>
            </IconButton>
          </Box>
          <div className={ classes.modeToggleGroup }>
            { this.renderRoutingModeSelector() }
          </div>
        </Toolbar>
        { this.renderRoutingForm() }
        <AirQualitySlider routeTotalExposures={ displayedExposures || [] } displayRouteAirQuality={ displayAirQualitySection } loadingRouteAirQuality={ loadingRouteAirQuality }/>
        <SavedRoutes
          savedRoutes={ userSavedRoutes }
          showSavedRoutes={ !!accessToken }
          onDeleteUserSavedRoute={ this.onDeleteUserSavedRoute }
          onUserRouteSelect={ this.onUserRouteSelect }
        />
        <FavouriteLocations
          savedFavouriteLocations={ userFavouriteLocations }
          showFavouriteLocations={ !!accessToken }
          onDeleteUserFavouriteLocation={ this.onDeleteUserFavouriteLocation }
          onUserLocationSelect={ this.onUserLocationSelect }
        />
        <PollutantSelection
          showControl={ WMSLayerVisible }
          pollutants={ pollutants }
          selectedPollutant={ selectedPollutant }
          onPollutantSelect={ this.onPollutantSelect }
        />
      </>
    );
  };

  /**
   * Renders air quality routing mode selector
   *
   * @param size component size
   */
  private renderRoutingModeSelector = (size?: number) => {
    const { defaultRoutingModes, selectedRoutingMode } = this.state;

    const customRoutingMode = { name: "Custom" } as DefaultRoutingMode;

    return (
      <>
        { defaultRoutingModes.map(defaultRoutingMode => {
          const opacity = defaultRoutingMode.id !== selectedRoutingMode?.id ? 0.6 : 1;

          return (
            <IconButton
              style={{
                opacity: opacity,
                width: size,
                height: size,
                marginLeft: size && size / 4,
                marginRight: size && size / 4
              }}
              key={ defaultRoutingMode.name }
              onClick={ () => this.onRoutingModeClick(defaultRoutingMode) }
            >
              <RoutingModeIconRenderer
                iconName={ defaultRoutingMode.icon }
                htmlColor="#fff"
              />
            </IconButton>
          );
        })
        }
        {/* TODO fix custom routing mode */}
        <IconButton
          style={{
            opacity: customRoutingMode.name !== selectedRoutingMode?.name ? 0.6 : 1,
            width: size,
            height: size,
            marginLeft: size && size / 4,
            marginRight: size && size / 4
          }}
          key={ defaultRoutingModes.length }
          onClick={ () => this.onRoutingModeClick(customRoutingMode) }
        >
          <StarBorder htmlColor="#fff"/>
        </IconButton>
      </>
    );
  };

  /**
   * Event handler for selecting the air quality routing mode
   *
   * @param defaultRoutingMode clicked default routing mode
   */
  private onRoutingModeClick = (defaultRoutingMode: DefaultRoutingMode) => {
    const { selectedRoutingMode, routeAlts } = this.state;

    if (selectedRoutingMode?.name === defaultRoutingMode.name && routeAlts.length) {
      const selectedRouteAlt = routeAlts.find(routeAlt => routeAlt.name === selectedRoutingMode.name) || routeAlts[0];
      this.onRouteOptionSelected(selectedRouteAlt);
      return;
    }

    this.setState({
      selectedRoutingMode: defaultRoutingMode
    });
  };

  /**
   * Displays already saved route
   *
   * @param routeToDisplay route to display
   */
  private displaySavedRoute = async (routeToDisplay: Route) => {
    const routeRaw = PolyUtil.decode(routeToDisplay.routePoints) as number[][];
    const route: RouteData = {
      lineCoordinates: routeRaw.map(coordinate => new LatLng(coordinate[0], coordinate[1]))
    };
    const locationFromCoordinates = route.lineCoordinates[0];
    const locationToCoordinates = route.lineCoordinates[route.lineCoordinates.length - 1];

    const locationFrom = { coordinates: locationFromCoordinates, name: routeToDisplay.locationFromName };
    const locationTo = { coordinates: locationToCoordinates, name: routeToDisplay.locationToName };

    const newState = {
      route: route,
      locationFrom: locationFrom,
      locationTo: locationTo,
      loadingRouteAirQuality: true,
      displayAirQualitySection: true,
      locationFromTextInput: routeToDisplay.locationFromName,
      locationToTextInput: routeToDisplay.locationToName,
      routeAlts: [],
      mapViewport: { center: [ locationFromCoordinates.lat, locationFromCoordinates.lng ] as [number, number], zoom: 13 }
    };
    this.setState(newState);
    const updatedRoute = await this.updateRouteAirQualityData(route);

    this.setState({
      route: updatedRoute,
      loadingRouteAirQuality: false
    });
  };

  /**
   * Displays already saved location
   *
   * @param locationToDisplay location to display
   */
  private displayFavouriteLocation = (locationToDisplay: FavouriteLocation) => {
    if (!locationToDisplay) {
      return;
    }

    const coordinates = new LatLng(locationToDisplay.latitude, locationToDisplay.longitude);
    const locationObject = { name: locationToDisplay.name, coordinates: coordinates };
    this.setState({
      mapViewport: {
        center: [locationToDisplay.latitude, locationToDisplay.longitude],
        zoom: 15
      },
      selectedFavouriteLocation: locationObject
    });
  };

  /**
   * Loads user settings
   */
  private loadUserSettings = async () => {
    const { accessToken } = this.props;

    if (!accessToken) {
      return;
    }

    this.setState({ loadingUserSettings: true });
    try {
      const userSettingsApi = Api.getUsersApi(accessToken);
      const user = await userSettingsApi.getUser({ userId: accessToken.userId || "" });

      if (user.homeLocation) {
        const { latitude, longitude, address } = user.homeLocation;
        let center;
        if (latitude && longitude) {
          center = [ latitude, longitude ] as [number, number];
        } else if (address) {
          center = await MapUtils.getLocation(address);
        }
        if (center) {
          const mapViewport = { center: center, zoom: 13 };
          this.setState({ mapViewport: mapViewport });
        }
      }
    } catch (error) {
      console.log(error);
    }
    this.setState({ loadingUserSettings: false });
  };

  /**
   * Filters favourite locations with a specific keyword
   *
   * @param keyword the string to search
   */
  private searchInFavouriteLocations = (keyword:string) => {
    const { userFavouriteLocations } = this.state;
    return userFavouriteLocations.filter(location =>
      location.name
        .toLowerCase()
        .startsWith(keyword.toLowerCase()));
  };

  /**
   * Adds users saved locations in location from and location in options.
   */
  private setLocationOptions = () => {
    const { userFavouriteLocations } = this.state;
    const locationToOptions = MapScreen.mapLocationsFromFavouriteLocations(userFavouriteLocations);
    const locationFromOptions = locationToOptions;
    this.setState({ locationFromOptions: locationFromOptions, locationToOptions: locationToOptions });
  };

  /**
   * Loads pollutants
   */
  private getPollutants = async () => {
    try {
      const pollutants = await Api.getPollutantsApi().getPollutants({});
      this.setState({
        pollutants: pollutants
      });
    } catch (error) {
      this.setState({
        error: error
      });
    }
  };

  /**
   * Loads default routing modes
   */
  private getDefaultRoutingModes = async () => {
    try {
      const defaultRoutingModes = await Api.getDefaultRoutingModeApi().getRoutingModes({});

      this.setState({
        defaultRoutingModes: defaultRoutingModes
      });
    } catch (error) {
      this.setState({
        error: error
      });
    }
  };

  /**
   * Loads user saved routes
   */
  private getUserSavedRoutes = async () => {
    const { accessToken } = this.props;

    if (!accessToken) {
      return;
    }

    const routesApi = Api.getRoutesApi(accessToken);
    const userRoutes = await routesApi.listRoutes({});

    this.setState({
      userSavedRoutes: userRoutes
    });
  };

  /**
   * Deletes user saved route
   *
   * @param routeId route Id string
   */
  private onDeleteUserSavedRoute = async (routeId: string) => {
    const { accessToken } = this.props;
    const { userSavedRoutes } = this.state;

    if (!accessToken) {
      return;
    }

    const routesApi = Api.getRoutesApi(accessToken);
    await routesApi.deleteRoute({ routeId: routeId });

    this.setState({
      userSavedRoutes: userSavedRoutes.filter(userSavedRoute => userSavedRoute.id !== routeId)
    });
  };

  /**
   * Loads user favourite locations
   */
  private getUserFavouriteLocations = async () => {
    const { accessToken } = this.props;

    if (!accessToken) {
      return;
    }

    const locationsApi = Api.getLocationsApi(accessToken);
    const userFavouriteLocations = await locationsApi.listUserFavouriteLocations();

    this.setState({ userFavouriteLocations: userFavouriteLocations });
  };

  /**
   * Deletes user favourite location
   *
   * @param locationId location Id string
   */
  private onDeleteUserFavouriteLocation = async (locationId: string) => {
    const { accessToken } = this.props;
    const { userFavouriteLocations } = this.state;

    if (!accessToken) {
      return;
    }

    const locationsApi = Api.getLocationsApi(accessToken);
    await locationsApi.deleteUserFavouriteLocation({ favouriteId: locationId });

    this.setState({
      userFavouriteLocations: userFavouriteLocations.filter(userSavedLocation => userSavedLocation.id !== locationId)
    });
  };

  /**
   * Renders the form for routing
   */
  private renderRoutingForm = (): JSX.Element => {
    const { classes, accessToken } = this.props;
    const {
      locationFrom,
      locationTo,
      departureDate,
      departureTime,
      locationFromOptions,
      locationToOptions,
      locationFromTextInput,
      locationToTextInput,
      loadingRoute,
      savingRoute,
      route
    } = this.state;

    return (
      <div className={ classes.routingForm }>
        <div className={ classes.inputFieldContainer }>
          <Autocomplete
            filterOptions={ options => options }
            onInputChange={ this.onLocationFromChange }
            inputValue={ locationFromTextInput }
            onChange={ this.onLocationFromSelected }
            options={ locationFromOptions }
            getOptionLabel={ (option: Location) => option.name || "" }
            getOptionSelected={ (option, value) => option.name === value.name }
            value={ locationFrom }
            renderInput={ params =>
              <div ref={ params.InputProps.ref } className={ classes.autoCompleteInputWrapper }>
                <MyLocation fontSize="small" htmlColor="#FFF"/>
                <TextField
                  variant="standard"
                  className={ classes.routingFormInput }
                  placeholder={ strings.from }
                  { ...params }
                  fullWidth
                />
              </div>
            }
          />
          <Autocomplete
            filterOptions={ options => options }
            onInputChange={ this.onLocationToChange }
            inputValue={ locationToTextInput }
            onChange={ this.onLocationToSelected }
            options={ locationToOptions }
            getOptionLabel={ (option: Location) => option.name || "" }
            getOptionSelected={ (option, value) => option.name === value.name }
            value={ locationTo }
            style={{ marginTop: theme.spacing(2) }}
            renderInput={ params =>
              <div ref={ params.InputProps.ref } className={ classes.autoCompleteInputWrapper }>
                <LocationOn fontSize="small" htmlColor="#FFF"/>
                <TextField
                  variant="standard"
                  className={ classes.routingFormInput }
                  placeholder={ strings.to }
                  { ...params }
                  fullWidth
                />
              </div>
            }
          />
          <Box mt={ 4 }>
            <Divider light/>
          </Box>
          <Box mt={ 2 }>
            <MuiPickersUtilsProvider utils={ MomentUtils }>
              <KeyboardDatePicker
                todayLabel={ strings.timePicker.today}
                cancelLabel={ strings.timePicker.cancel }
                inputVariant="standard"
                showTodayButton
                className={ classes.keyboardTimePicker }
                fullWidth
                label={ strings.departureDate }
                variant="dialog"
                format="MM.DD.yyyy"
                value={ departureDate }
                onChange={ this.onStartDateChange }
                disableToolbar={ true }
              />
            </MuiPickersUtilsProvider>
          </Box>
          <Box mt={ 2 }>
            <MuiPickersUtilsProvider utils={ MomentUtils }>
              <KeyboardTimePicker
                cancelLabel={ strings.timePicker.cancel }
                inputVariant="standard"
                className={ classes.keyboardTimePicker }
                fullWidth
                ampm={ false }
                label={ strings.departureTime }
                variant="dialog"
                keyboardIcon={ <AccessTimeIcon/> }
                value={ departureTime }
                onChange={ this.onStartTimeChange }
                KeyboardButtonProps={{
                  "aria-label": "change time"
                }}
              />
            </MuiPickersUtilsProvider>
          </Box>
        </div>
        <div className={ classes.routingControls }>
          <Box display="flex">
            <Button
              variant="outlined"
              onClick={ this.updateRoute }
              className={ classes.routingFormButton }
              endIcon={
                loadingRoute ?
                  <CircularProgress size={ 20 } color="inherit" className={ classes.routingFormLoader }/>
                  :
                  <Search htmlColor="#fff"/>
              }
            >
              { strings.routes.findRoute }
            </Button>
            <Button
              variant="outlined"
              onClick={ this.cancelAll }
              className={ classes.cancelFormButton }
              endIcon={ <Clear htmlColor="#fff"/> }
            >
              { strings.routes.cancelButton }
            </Button>
          </Box>
          { accessToken && route &&
            <Box display="flex" flexDirection="column">
              <Button
                variant="outlined"
                onClick={ this.onSaveRouteClick }
                className={ classes.routingFormButton }
                endIcon={
                  savingRoute ?
                    <CircularProgress size={ 20 } color="inherit" className={ classes.routingFormLoader }/>
                    :
                    <Save htmlColor="#fff"/>
                }
              >
                { strings.routes.saveRoute }
              </Button>
              <Button
                variant="outlined"
                onClick={ this.onNavigationCompleteClick }
                className={ classes.routingFormButton }
                endIcon={ <Check htmlColor="#fff"/> }
              >
                { strings.routes.navigated }
              </Button>
            </Box>
          }
        </div>
      </div>
    );
  };

  /**
   * Renders the map
   */
  private renderMap = (): JSX.Element => {
    const { classes } = this.props;
    const {
      route,
      selectedFavouriteLocation,
      mapInteractive,
      WMSLayerVisible,
      routeAlts,
      selectedRoutingMode,
      selectedPollutant,
      departureDate,
      departureTime,
      pollutants,
      mapViewport,
      mapAirQuality,
      pollutantControlMapCenter
    } = this.state;

    // TODO change to real air quality data
    const coloredPollutant = pollutants[0];
    const selectedRouteAlt = routeAlts.find(routeAlt => routeAlt.name === selectedRoutingMode?.name);

    return (
      <Map
        className={
          classNames(
            classes.mapComponent,
            classes.markerPopup,
            this.validateRoutingModeRoutes() && classes.markerPopupBottom
          )}
        ref={ this.mapRef }
        zoomControl={ false }
        ondblclick={ this.addRoutePoint }
        doubleClickZoom={ false }
        onViewportChange={ this.onViewportChange }
        viewport={ mapViewport }
        scrollWheelZoom={ mapInteractive }
        dragging={ mapInteractive }
        ondragend={ this.updatePollutantControl }
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
        />

        { route &&
          <ColoredRoute
            baseColor="#0288d1"
            coordinates={ route.lineCoordinates }
            airQualityEntries={ route.routeAirQuality }
            coloredPollutant={ coloredPollutant }
          />
        }
        {
          routeAlts.map(routeAlt => (
            routeAlt.associatedRouteData && (
              <ColoredRoute
                coordinates={ routeAlt.associatedRouteData.lineCoordinates }
                airQualityEntries={ routeAlt.associatedRouteData.routeAirQuality }
                selected={ selectedRoutingMode?.name === routeAlt.name }
                onClick={ () => this.onRouteOptionSelected(routeAlt) }
                coloredPollutant={ coloredPollutant }
              />
            )
          ))
        }
        { selectedFavouriteLocation?.coordinates &&
          <Marker
            ref={ MapScreen.openPopup }
            position={ selectedFavouriteLocation.coordinates }
          >
            <Popup
              onOpen={ this.switchMapInteraction(false) }
              onClose={ this.exitSelectedFavouriteLocation }
              autoPan={ false }
            >
              <h3>
                { selectedFavouriteLocation.name }
              </h3>
              <Button onClick={ this.onSetSourceClick(selectedFavouriteLocation.coordinates, selectedFavouriteLocation.name) }>
                { strings.from }
              </Button>
              <Button onClick={ this.onSetDestinationClick(selectedFavouriteLocation.coordinates, selectedFavouriteLocation.name) }>
                { strings.to }
              </Button>
            </Popup>
          </Marker>
        }
        { this.renderLocationFromMarker(selectedRouteAlt) }
        { this.renderLocationToMarker() }
        { WMSLayerVisible && (selectedPollutant !== undefined) &&
          <WMSTileLayer
            format="image/jpeg"
            transparent={ true }
            layers={PollutantUtils.getWMSLayers(selectedPollutant)}
            url={ process.env.REACT_APP_GEO_SERVER_URL || "" }
            opacity={ 0.5 }
            TIME={ PollutantUtils.getTimeInISO(departureDate, departureTime) }
          />
        }

        <MapControl
          parentMapRef={ this.mapRef }
          parentLayerRef={ this.overlayRef }
          airQuality={ mapAirQuality }
          pollutants={ pollutants }
          pollutantControlMapCenter={ pollutantControlMapCenter }
          changeWMSLayerVisibility={ this.changeWMSLayerVisibility }
        />

      </Map>
    );
  };

  /**
   * Renders save route dialog
   */
  private renderSaveRouteConfirmDialog = () => {
    const { savingRoute } = this.state;
    return (
      <ConfirmDialog
        title={ strings.routes.saveRoute }
        positiveButtonText={ strings.common.save }
        cancelButtonText={ strings.common.cancel }
        dialogVisible={ savingRoute }
        onDialogConfirm={ this.onRouteSaveConfirm }
        onDialogCancel={ this.onRouteSaveCancel }
        userInput={ this.renderDialogNameInput }
      />
    );
  };

  /**
   * Renders save location dialog
   */
  private renderSaveLocationConfirmDialog = () => {
    const { savingLocation } = this.state;
    return (
      <ConfirmDialog
        title={ strings.locations.saveLocation }
        positiveButtonText={ strings.common.save }
        cancelButtonText={ strings.common.cancel }
        dialogVisible={ savingLocation }
        onDialogConfirm={ this.onLocationSaveConfirm }
        onDialogCancel={ this.onLocationSaveCancel }
        userInput={ this.renderDialogNameInput }
      />
    );
  };

  /**
   * Renders duplicate location dialog
   */
  private renderDuplicateLocationConfirmDialog = () => {
    const { duplicateLocation } = this.state;
    return (
      <ConfirmDialog
        title={ strings.locations.duplicateLocation }
        dialogVisible={ !!duplicateLocation }
        text={
          <>
            <Typography>
              { strings.locations.duplicateLocationWarning }
            </Typography>
            <br/>
            <Typography style={{ fontWeight: 500, fontSize: 17 }}>
              { strings.savedLocationsName }
              :
              <i>{ duplicateLocation?.name }</i>
            </Typography>
            <Typography>
              { `${strings.savedLocationsLatitude}: ${duplicateLocation?.latitude}, ${strings.savedLocationsLongtitude}: ${duplicateLocation?.longitude}` }
            </Typography>
          </>
        }
        cancelButtonText={ strings.common.close }
        onDialogCancel={ this.exitDuplicateLocation }
      />
    );
  };

  /**
   * Renders user input field for dialog
   */
  private renderDialogNameInput = () => {
    const { userDialogInput } = this.state;
    return (
      <TextField
        label={ strings.routes.routeNameInput }
        value={ userDialogInput }
        onChange={ this.onDialogNameInputChange }
      />
    );
  };

  /**
   * Renders route selection popup content
   *
   * @param selectedRouteAlt selected route alt
   */
  private renderRouteSelectionPopupContent = (selectedRouteAlt?: RoutingModeData) => {
    const { classes } = this.props;

    return (
      <div>
        <div className={ classes.markerPopupInfoText }>
          <h4 className={ classes.markerPopupRouteDuration }>
            { selectedRouteAlt?.associatedRouteData?.duration || "" }
          </h4>
          <h4 className={ classes.markerPopupRoutePollution }>
            {/* TODO: change to the real route exposure value */}
            72pi
          </h4>
        </div>
        <div className={ classes.popupRoutingMode }>
          { this.renderRoutingModeSelector(20) }
        </div>
        <h3 className={ classes.markerPopupRoutingModeText }>
          { selectedRouteAlt?.name }
        </h3>
      </div>
    );
  };

  /**
   * Renders location from popup content
   */
  private renderLocationFromPopupContent = () => {
    const {
      locationFrom,
      locationFromTextInput
    } = this.state;

    if (!locationFrom?.coordinates) {
      return;
    }

    return (
      <div>
        <h3>
          { `${strings.from}:` }
        </h3>
        <p>
          { locationFromTextInput || "" }
        </p>
        <Button onClick={ () => this.onSaveLocationClick(locationFrom.coordinates) }>
          { strings.locations.saveLocation }
        </Button>
      </div>
    );
  };

  /**
   * Renders location to marker
   */
  private renderLocationToMarker = () => {
    const { locationTo, locationToTextInput } = this.state;

    if (!locationTo?.coordinates) {
      return;
    }

    if (this.validateRoutingModeRoutes()) {
      return (
        <Marker
          ref={ ref => ref?.leafletElement.unbindPopup() }
          position={ locationTo.coordinates }
          icon={ destinationIcon }
        />
      );
    }

    return (
      <Marker position={ locationTo.coordinates } icon={ destinationIcon }>
        <Popup
          onOpen={ this.switchMapInteraction(false) }
          onClose={ this.switchMapInteraction(true) }
          autoPan={ false }
        >
          <h3>
            { `${strings.to}:` }
          </h3>
          <p>
            { locationToTextInput || "" }
          </p>
          <Button onClick={ () => this.onSaveLocationClick(locationTo.coordinates) }>
            { strings.locations.saveLocation }
          </Button>
        </Popup>
      </Marker>
    );
  };

  /**
   * Renders location from marker
   *
   * @param selectedRouteAlt selected route alt
   */
  private renderLocationFromMarker = (selectedRouteAlt?: RoutingModeData) => {
    const { locationFrom, selectedRoutingMode } = this.state;
    const showRoutePicker = this.validateRoutingModeRoutes() && selectedRoutingMode;

    if (!locationFrom?.coordinates) {
      return;
    }

    return (
      <Marker
        ref={ this.sourceMarkerRef }
        position={ locationFrom.coordinates }
        icon={ sourceIcon }
      >
        <Popup
          onOpen={ this.switchMapInteraction(false) }
          onClose={ this.switchMapInteraction(true) }
          autoPan={ false }
        >
          { showRoutePicker ?
            this.renderRouteSelectionPopupContent(selectedRouteAlt) :
            this.renderLocationFromPopupContent()
          }
        </Popup>
      </Marker>
    );
  };

  /**
   * Validate all fetched routing mode routes
   */
  private validateRoutingModeRoutes = () => {
    const { routeAlts } = this.state;

    return !!routeAlts.length && routeAlts.every(routeAlt => routeAlt.associatedRouteData?.lineCoordinates.length);
  };

  /**
   * Opens popup when component renders
   *
   * @param marker marker
   */
  static openPopup = (marker: Marker) => {
    if (marker && marker.leafletElement) {
      window.setTimeout(() => {
        marker.leafletElement.openPopup();
      });
    }
  };

  /**
   * Handles save route button click
   */
  private onSaveRouteClick = () => {
    this.setState({
      savingRoute: true
    });
  };

  /**
   * Handles navigation complete button click
   * TODO: to be removed for pre-production
   */
  private onNavigationCompleteClick = async () => {
    const { accessToken } = this.props;
    const { route } = this.state;

    if (!route?.totalExposures) {
      return;
    }

    const exposureEndedTime = moment();
    const exposureStartedTime = exposureEndedTime.subtract(route?.duration);

    const routePollutionValue: PollutionEntry[] = route.totalExposures.map(totalExposures => ({
      pollutantId: totalExposures.pollutant.id,
      value: totalExposures.pollutionValue
    } as PollutionEntry));

    const routeExposureInstance: RoutePollutionExposureRecord = {
      startedAt: exposureStartedTime.toDate(),
      pollutionDataTotals: routePollutionValue,
      endedAt: exposureEndedTime.toDate()
    };

    try {
      await Api.getExposureInstancesApi(accessToken).createRoutePollutionExposureRecord({
        routePollutionExposureRecord: routeExposureInstance
      });

      this.cancelAll();
    } catch (error) {
      this.setState({
        error: error
      });
    }
  };

  /**
   * Handles one route option selection action
   *
   * @param routingMode routing mode
   */
  private onRouteOptionSelected = (routeAlt: RoutingModeData) => {
    this.sourceMarkerRef.current?.leafletElement.closePopup();

    this.setState({ route: routeAlt.associatedRouteData });
    const polyline = routeAlt.associatedRouteData ? PolyUtil.encode(routeAlt.associatedRouteData.lineCoordinates) as string : "";

    this.setState({
      polyline: polyline,
      routeAlts: []
    });
  };

  /**
   * Matches pollutant control map center with the main map center
   */
  private updatePollutantControl = () => {
    const { mapViewport } = this.state;

    if (!mapViewport.center) {
      return;
    }

    this.setState({
      pollutantControlMapCenter: mapViewport.center
    });
  };

  /**
   * Changes WMS layer visibility
   */
  private changeWMSLayerVisibility = () => {
    const { WMSLayerVisible } = this.state;

    this.setState({
      WMSLayerVisible: !WMSLayerVisible
    });
  };

  /**
   * Handles save route confirm dialog button click
   */
  private onRouteSaveConfirm = async () => {
    const { accessToken } = this.props;
    const { polyline, locationFrom, locationTo, userDialogInput, userSavedRoutes } = this.state;

    if (!accessToken || !polyline || !locationFrom || !locationTo || !userDialogInput) {
      return;
    }

    try {
      const routesApi = Api.getRoutesApi(accessToken);
      const createdRoute = await routesApi.createRoute({
        route: {
          name: userDialogInput,
          isPublic: false,
          transportationType: TransportationType.WALKING,
          routePoints: polyline,
          locationFromName: locationFrom?.name,
          locationToName: locationTo?.name,
          locationNames: `${locationFrom?.name}-${locationTo?.name}`,
          routeHistoryType: RouteHistoryType.FAVORITE,
          savedAt: new Date()
        }
      });
      this.setState({
        userSavedRoutes: userSavedRoutes.concat(createdRoute),
        savingRoute: false,
        userDialogInput: ""
      });
    } catch (error) {
      this.setState({
        savingRoute: false,
        error: error
      });
    }
  };

  /**
   * Action handler for route name input
   *
   * @param action input action
   */
  private onDialogNameInputChange = (action: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    this.setState({
      userDialogInput: action.target.value
    });
  };

  /**
   * Handles save route dialog cancel button click
   */
  private onRouteSaveCancel = () => {
    this.setState({
      savingRoute: false,
      userDialogInput: ""
    });
  };

  /**
   * Handles save location button click
   *
   * @param coordinates coordinates string
   */
  private onSaveLocationClick = (coordinates: LatLng) => {
    this.setState({
      savingLocation: true,
      savingLocationCoordinates: coordinates
    });
  };

  /**
   * Handles save location confirm dialog button click
   */
  private onLocationSaveConfirm = async () => {
    const { accessToken } = this.props;
    const { userDialogInput, userFavouriteLocations, savingLocationCoordinates } = this.state;

    if (!accessToken || !userDialogInput || !savingLocationCoordinates) {
      return;
    }

    const duplicateLocations = this.getDuplicateLocations(savingLocationCoordinates);

    if (duplicateLocations.length > 0) {
      const duplicateLocation = duplicateLocations[0];
      this.setState({
        duplicateLocation: duplicateLocation
      });
      return;
    }

    try {
      const geocodingResponse = await Geocode.fromLatLng(savingLocationCoordinates.lat.toString(), savingLocationCoordinates.lng.toString(), process.env.REACT_APP_GOOGLE_API_KEY);

      const geocodingResultComponents = geocodingResponse.results[0]?.address_components;
      const route = geocodingResultComponents.find((component: any) => component.types.includes("route"));
      const streetNumber = geocodingResultComponents.find((component: any) => component.types.includes("street_number"));
      const city = geocodingResultComponents.find((component: any) => component.types.includes("locality"));
      const postalCode = geocodingResultComponents.find((component: any) => component.types.includes("postal_code"));
      const country = geocodingResultComponents.find((component: any) => component.types.includes("country"));

      const favouriteLocationAddress = geocodingResultComponents ? {
        streetAddress: `${route?.short_name || ""} ${streetNumber?.short_name || ""}`,
        city: city?.short_name,
        postalCode: postalCode?.short_name,
        country: country?.long_name
      } : undefined;

      const locationsApi = Api.getLocationsApi(accessToken);
      const createdLocation = await locationsApi.createUserFavouriteLocation({
        favouriteLocation: {
          name: userDialogInput,
          latitude: savingLocationCoordinates.lat,
          longitude: savingLocationCoordinates.lng,
          address: favouriteLocationAddress
        }
      });
      this.setState({
        userFavouriteLocations: userFavouriteLocations.concat(createdLocation),
        savingLocationCoordinates: undefined,
        savingLocation: false,
        userDialogInput: ""
      });
    } catch (error) {
      this.setState({
        savingLocation: false,
        error: error
      });
    }
  };

  /**
   * Utility method for getting duplicate locations from the userFavouriteLocations array
   *
   * @param locationToCompare location to be compared
   */
  private getDuplicateLocations = (locationToCompare: LatLng) => {
    const { userFavouriteLocations } = this.state;

    /**
   * compareCoordinate
   *
   * @param x: number
   * @param y: number
   */
    const compareCoordinate = (x: number, y: number) => Math.abs(x - y) <= this.COORDINATE_DELTA;

    return userFavouriteLocations.filter(
      location => (
        (location.latitude !== undefined && compareCoordinate(location.latitude, locationToCompare.lat)) &&
        (location.longitude !== undefined && compareCoordinate(location.longitude, locationToCompare.lng))
      )
    );
  };

  /**
   * Handles save location dialog cancel button click
   */
  private onLocationSaveCancel = () => {
    this.setState({
      savingLocation: false,
      userDialogInput: ""
    });
  };

  /**
   * Creates a route list offset
   *
   * @param routeLine the route line as array of points
   * @returns modified route line
   */
  static routeOffsets = (routeAlts: RoutingModeData[]): RoutingModeData[] => {
    if (!routeAlts.length) {
      return routeAlts;
    }

    const routeAltsOffsetAdjusted = [ ...routeAlts ];

    const routeInterval = 0.00003;
    const offsetRange = routeInterval * routeAlts.length;
    const initialOffset = -(offsetRange / 2);

    for (let i = 0; i < routeAlts.length; i++) {
      const routeCoors = routeAltsOffsetAdjusted[i].associatedRouteData!.lineCoordinates;

      if (routeCoors) {
        const updatedRouteData: RouteData = {
          ...routeAltsOffsetAdjusted[i].associatedRouteData,
          lineCoordinates: MapScreen.routeOffset(routeCoors, initialOffset + routeInterval * i)
        };

        routeAltsOffsetAdjusted[i] = {
          ...routeAltsOffsetAdjusted[i],
          associatedRouteData: updatedRouteData
        };
      }
    }
    return routeAltsOffsetAdjusted;
  };

  /**
   * Insert air quality data into the routing mode data array
   *
   * @param routeLine the route line as array of points
   * @returns modified route line
   */
  private insertRouteAltsAirQuality = async (routeAlts: RoutingModeData[]) => {
    if (!routeAlts.length) {
      return routeAlts;
    }

    this.setState({
      loadingRouteAirQuality: true
    });

    const updatedRouteAlts: RoutingModeData[] = [];
    for (let i = 0; i < routeAlts.length; i++) {
      const routeAlt = routeAlts[i];
      const updatedRouteData = routeAlt.associatedRouteData ? await this.updateRouteAirQualityData(routeAlt.associatedRouteData) : routeAlt.associatedRouteData;
      const updatedRouteAlt: RoutingModeData = {
        ...routeAlt,
        associatedRouteData: updatedRouteData
      };
      updatedRouteAlts.push(updatedRouteAlt);
    }

    this.setState({
      routeAlts: updatedRouteAlts,
      loadingRouteAirQuality: false
    });
  };

  /**
   * Insert from and to location to a list of route
   *
   * @param routeAlts list of alternative routes
   * @param fromLocation from location
   * @param fromLocation to location
   * @returns routes with from and to location inserted
   */
  static insertFromToLocations = (routeAlts: RoutingModeData[], fromLocation: LatLng, toLocation: LatLng): RoutingModeData[] => {
    if (!routeAlts.length) {
      return routeAlts;
    }

    routeAlts.forEach(routeAlt => {
      if (!routeAlt.associatedRouteData) {
        return;
      }

      routeAlt.associatedRouteData.lineCoordinates.unshift(fromLocation);
      routeAlt.associatedRouteData.lineCoordinates.push(toLocation);
    });

    return routeAlts;
  };

  /**
   * Creates a route line offset
   *
   * @param routeLine the route line as array of points
   * @param offsetDistance coordinates offset distance
   * @returns modified route line
   */
  static routeOffset = (routeLine: LatLng[], offsetDistance: number): LatLng[] => {
    if (!routeLine.length) {
      return [];
    }

    const firstPoint = MapScreen.convertPointToCoordinates(routeLine[0]);
    const lastPoint = MapScreen.convertPointToCoordinates(routeLine[routeLine.length - 1]);
    const segmentAngle = Math.atan2(firstPoint.lat - lastPoint.lat, firstPoint.lng - lastPoint.lng);
    const offsetAngle = segmentAngle - Math.PI / 2;
    return routeLine.map(p => {
      const point = MapScreen.convertPointToCoordinates(p);
      const x = point.lng + offsetDistance * Math.cos(offsetAngle);
      const y = point.lat + offsetDistance * Math.sin(offsetAngle);
      return new LatLng(y, x);
    });
  };

  /**
   * Converts point to coordinates
   *
   * @param point point
   * @returns custom formatted point
   */
  static convertPointToCoordinates = (point: LatLng): PointCoordinates => {
    const pointString = point.toString().split(",");

    return {
      lat: parseFloat(pointString[0]),
      lng: parseFloat(pointString[1])
    };
  };

  /**
   * Fires when a new locationFrom is selected
   *
   * @param event React event
   * @param locationFrom a new value for locationFrom
   * @param reason autocomplete change reason
   */
  private onLocationFromSelected = (event: React.ChangeEvent<{}>, locationFrom: Location | null | undefined, reason: AutocompleteChangeReason) => {
    let locationFromValue = locationFrom;
    if (locationFrom === null) {
      locationFromValue = undefined;
    }
    this.setState({ locationFrom: locationFromValue as (Location | undefined) });
  };

  /**
   * Fires when the value of the text input for locationFrom changes and updates the list of options
   *
   * @param event React event
   * @param locationFromTextInput a new value for the text input for locationFrom
   * @param reason autocomplete change reason
   */
  private onLocationFromChange = async (event: ChangeEvent<{}>, locationFromTextInput: string, reason: AutocompleteInputChangeReason) => {
    this.setState({ locationFromTextInput: locationFromTextInput });
    try {
      const locationFromOptions = await (this.nominateCall(locationFromTextInput));
      locationFromOptions.unshift(...MapScreen.mapLocationsFromFavouriteLocations(this.searchInFavouriteLocations(locationFromTextInput)));
      this.setState({ locationFromOptions: locationFromOptions });
    } catch (error) {
      this.setState({
        locationFromOptions: [],
        error: error
      });
    }
  };

  /**
   * Fires when a new locationTo is selected
   *
   * @param event React event
   * @param locationTo a new value for locationTo
   * @param reason autocomplete change reason
   */
  private onLocationToSelected = (event: React.ChangeEvent<{}>, locationTo: Location | null | undefined, reason: AutocompleteChangeReason) => {
    let locationToValue = locationTo;
    if (locationTo === null) {
      locationToValue = undefined;
    }
    this.setState({ locationTo: locationToValue as (Location | undefined) });
  };

  /**
   * Fires when user changes the departure date
   *
   * @param date input date
   */
  private onStartDateChange = (date: MaterialUiPickersDate) => {
    if (!date) {
      return null;
    }

    this.setState({
      departureDate: date.toDate()
    });

    this.getAirQualityPollutantData();
  };

  /**
   * Fires when user changes the departure time
   *
   * @param date input date
   */
  private onStartTimeChange = (date: MaterialUiPickersDate) => {
    if (!date) {
      return null;
    }
    this.setState({
      departureTime: date.toDate()
    });

    this.getAirQualityPollutantData();
  };

  /**
   * Calls the api for similar locations and translates it to option format
   *
   * @param keyword the keyword to call the api
   */
  private nominateCall = async (keyword: string): Promise<Location[]> => {
    try {
      const geocodingResponse = await Geocode.fromAddress(keyword, process.env.REACT_APP_GOOGLE_API_KEY);
      return geocodingResponse.results.map((result: any) => {
        const { lat, lng } = result.geometry.location;
        const coordinates = `${lat},${lng}`;
        const name = result.formatted_address;
        return { name: name, coordinates: coordinates };
      });
    } catch (error) {
      this.setState({
        error: error
      });

      return [];
    }
  };

  /**
   * Fires when the value of the text input for locationTo changes and updates the list of options
   *
   * @param event React event
   * @param name a new value for the text input for locationTo
   * @param reason autocomplete change reason
   */
  private onLocationToChange = async (event: ChangeEvent<{}>, locationToTextInput: string, reason: AutocompleteInputChangeReason) => {
    this.setState({ locationToTextInput: locationToTextInput });
    try {
      const locationToOptions = await (this.nominateCall(locationToTextInput));
      locationToOptions.unshift(...MapScreen.mapLocationsFromFavouriteLocations(this.searchInFavouriteLocations(locationToTextInput)));
      this.setState({ locationToOptions: locationToOptions });
    } catch (error) {
      this.setState({
        locationToOptions: [],
        error: error
      });
    }
  };

  /**
   * Clear selected locations, routes
   */
  private cancelAll = () => {
    const { loadingRoute } = this.state;
    this.exitSelectedFavouriteLocation();

    if (loadingRoute) {
      return;
    }

    this.setState({
      route: undefined,
      routeAlts: [],
      locationFrom: undefined,
      locationTo: undefined,
      selectedFavouriteLocation: undefined,
      displayAirQualitySection: false
    });
  };

  /**
   * Requests a route from Open Trip Planner
   */
  private updateRoute = async () => {
    const { defaultRoutingModes } = this.state;

    if (!defaultRoutingModes.length) {
      return;
    }

    this.setState({
      loadingRoute: true,
      route: undefined,
      displayAirQualitySection: true
    });

    try {
      const { locationTo, locationFrom, mapViewport } = this.state;

      if (!locationFrom || !locationFrom?.coordinates || !locationTo || !locationTo?.coordinates) {
        this.setState({ loadingRoute: false, displayAirQualitySection: false });
        return;
      }

      const locationFromCoordinates = locationFrom.coordinates;
      const locationToCoordinates = locationTo.coordinates;

      const routeAltsRaw = await this.fetchRouteLines(defaultRoutingModes);

      const routeAltsOffsetAdjusted = MapScreen.routeOffsets(routeAltsRaw);

      const routeAlts = MapScreen.insertFromToLocations(routeAltsOffsetAdjusted, locationFromCoordinates, locationToCoordinates);

      this.sourceMarkerRef.current?.leafletElement.openPopup();

      this.setState({
        routeAlts: routeAlts,
        locationFrom: locationFrom,
        locationTo: locationTo,
        loadingRoute: false,
        mapViewport: { ...mapViewport, center: [locationFromCoordinates.lat, locationFromCoordinates.lng] }
      });

      this.insertRouteAltsAirQuality(routeAlts);
      this.selectDefaultRoutingMode();
    } catch (error) {
      this.clearRoutingDetails();

      this.setState({
        error: error
      });
    }
  };

  /**
   * Selects default routing mode
   */
  private selectDefaultRoutingMode = () => {
    const { defaultRoutingModes, selectedRoutingMode } = this.state;

    if (!defaultRoutingModes.length || selectedRoutingMode) {
      return;
    }
    const defaultModeName = process.env.REACT_APP_DEFAULT_ROUTING_MODE;

    this.setState({
      selectedRoutingMode: defaultRoutingModes.find(defaultRoutingMode => defaultRoutingMode.name === defaultModeName) || defaultRoutingModes[0]
    });
  };

  /**
   * Creates routing promises
   *
   * @param defaultRoutingMode defaultRoutingMode data
   * @returns routing promises
   */
  private createRoutingPromise = async (defaultRoutingMode: DefaultRoutingMode) => {
    const {
      locationTo,
      locationFrom,
      departureDate,
      departureTime,
      pollutants
    } = this.state;
    try {
      const constructedOTPUrl = RoutingUtils.OTPApiStringParser({
        defaultRoutingMode: defaultRoutingMode,
        pollutants: pollutants,
        time: moment(departureTime).format("h:mma"),
        date: moment(departureDate).format("MM-DD-yyyy"),
        maxWalkDistance: 100000,
        baseUrl: process.env.REACT_APP_OTP_URL,
        fromPlace: locationFrom?.coordinates,
        toPlace: locationTo?.coordinates
      });

      const routingResponse = await fetch(constructedOTPUrl);
      const jsonResponse = await routingResponse.json();
      const polyline = jsonResponse.plan.itineraries[0].legs[0].legGeometry.points;
      const duration = new Date(jsonResponse.plan.itineraries[0].duration * 1000).toISOString().substr(11, 5) + strings.units.hours;

      return {
        name: defaultRoutingMode.name,
        associatedRouteData: {
          lineCoordinates: PolyUtil.decode(polyline),
          duration: duration
        }
      } as RoutingModeData;
    } catch (error) {
      this.setState({
        error: error
      });
    }

    return {
      name: "",
      associatedRouteData: {
        lineCoordinates: [],
        duration: ""
      }
    };
  };

  /**
   * Returns a route line represented by array of coordinates
   *
   * @param defaultRoutingModes default routing mode array
   * @returns array of coordinates
   */
  private fetchRouteLines = async (defaultRoutingModes: DefaultRoutingMode[]): Promise<RoutingModeData[]> => {
    if (MapScreen.isOTPLowResources()) {
      const routeAltsRaw: RoutingModeData[] = [];
      for (let i = 0; i < defaultRoutingModes.length; i++) {
        const defaultRoutingMode = defaultRoutingModes[i];
        const result = await this.createRoutingPromise(defaultRoutingMode);
        routeAltsRaw.push(result);
      }

      return routeAltsRaw;
    }

    const routeAltsPromises = defaultRoutingModes.map(this.createRoutingPromise);

    return Promise.all([ ...routeAltsPromises ]);
  };

  /**
   * Updates air quality for route data
   *
   * @param routeData route data
   */
  private updateRouteAirQualityData = async (routeData: RouteData): Promise<RouteData> => {
    const { accessToken } = this.props;
    const { departureTime, pollutants } = this.state;

    const formattedRoute: string[] = routeData.lineCoordinates
      .map(point => {
        return `${point.lat},${point.lng}`;
      });

    try {
      const routeAirQuality = await Api.getAirQualityApi(accessToken).getAirQualityForCoordinatesArray({
        requestBody: formattedRoute,
        routingTime: departureTime
      });
      const routeTotalExposures: RouteTotalAirQuality[] = await Promise.all(
        routeAirQuality.pollutionDataTotals.filter(
          routePollutionEntry => !!routePollutionEntry.pollutantId
        ).map(
          async routePollutionEntry => {
            const foundPollutant = pollutants.find(pollutant => pollutant.id === routePollutionEntry.pollutantId)!;
            const meanRoutePollution = routePollutionEntry.value ? routePollutionEntry.value / routeAirQuality.airQualityEntries.length : 0;
            return {
              pollutant: foundPollutant,
              pollutionValue: meanRoutePollution
            } as RouteTotalAirQuality;
          }
        )
      );

      const updatedRouteData: RouteData = {
        ...routeData,
        totalExposures: routeTotalExposures,
        routeAirQuality: routeAirQuality.airQualityEntries
      };

      return updatedRouteData;
    } catch (error) {
      this.setState({
        error: error
      });
    }

    return routeData;
  };

  /**
   * Fires when the viewport of the map changes
   *
   * @param mapViewport a new viewport
   */
  private onViewportChange = async (mapViewport: Viewport) => {
    this.setState({
      mapViewport: mapViewport
    });
  };

  /**
   * Fires when user doubleclicks the map
   *
   * @param mouseEvent mouse event
   */
  private addRoutePoint = async (mouseEvent: LeafletMouseEvent) => {
    const { editingLocationFrom } = this.state;
    const position = mouseEvent.latlng;
    const location = {
      coordinates: position,
      name: `${position.lat.toString()}, ${position.lng.toString()}`
    };
    let geocodingResponse = null;
    if (editingLocationFrom) {
      const locationFromOptions = [location];
      try {
        geocodingResponse = await Geocode.fromLatLng(mouseEvent.latlng.lat.toString(), mouseEvent.latlng.lng.toString(), process.env.REACT_APP_GOOGLE_API_KEY);
      } catch (e) {
        console.warn("Geocoding error is ", e);
      }

      this.clearRoutingDetails();

      this.setState({
        locationFromOptions: locationFromOptions,
        locationFrom: location,
        editingLocationFrom: false
      });

      if (geocodingResponse !== null) {
        this.setState({
          locationFromTextInput: geocodingResponse.results[0].formatted_address
        });
      }
    } else {
      try {
        geocodingResponse = await Geocode.fromLatLng(mouseEvent.latlng.lat.toString(), mouseEvent.latlng.lng.toString(), process.env.REACT_APP_GOOGLE_API_KEY);
      } catch (e) {
        console.warn("Geocoding error is ", e);
      }

      this.setState({
        locationTo: location,
        editingLocationFrom: true
      });

      if (geocodingResponse !== null) {
        this.setState({
          locationToTextInput: geocodingResponse.results[0].formatted_address
        });
      }
    }
  };

  /**
   * Clears routing details
   */
  private clearRoutingDetails = () => {
    this.setState({
      locationFrom: undefined,
      locationTo: undefined,
      route: undefined,
      routeAlts: [],
      loadingRoute: false,
      displayAirQualitySection: false
    });
  };

  /**
   * Sets locations name from coordinates
   *
   * @param type GeocodeCoordinate type
   * @param lat lat string
   * @param lon lon string
   */
  private reverseGeocodeCoordinates = async (type: GeocodeCoordinate, lat: string, lon: string) => {
    try {
      const geocodingResponse = await Geocode.fromLatLng(lat, lon, process.env.REACT_APP_GOOGLE_API_KEY);
      const geocodedName = geocodingResponse.results[0].formatted_address || "";
      if (type === GeocodeCoordinate.To) {
        this.setState({
          locationToTextInput: geocodedName
        });
      }

      if (type === GeocodeCoordinate.From) {
        this.setState({
          locationFromTextInput: geocodedName
        });
      }
    } catch (e) {
      console.warn("Geocoding error is ", e);
    }
  };

  /**
   * Sets currently displayed route
   *
   * @param route route to display
   */
  private onUserRouteSelect = (route: Route) => {
    const sourceCoordinates = route.locationFromName.split(",");
    const destCoordinates = route.locationToName.split(",");
    this.reverseGeocodeCoordinates(GeocodeCoordinate.From, sourceCoordinates[0], sourceCoordinates[1]);
    this.reverseGeocodeCoordinates(GeocodeCoordinate.To, destCoordinates[0], destCoordinates[1]);
    this.displaySavedRoute(route);
  };

  /**
   * Sets currently displayed location
   *
   * @param location location to display
   */
  private onUserLocationSelect = (location: FavouriteLocation) => {
    this.displayFavouriteLocation(location);
  };

  /**
   * Sets status of selected pollutant for the WMS layer
   *
   * @param pollutant Pollutant to be selected
   */
  private onPollutantSelect = (pollutant: Pollutant) => {
    this.setState({
      selectedPollutant: pollutant
    });
  };

  /**
   * Turns selected favourite location to source location
   */
  private onSetSourceClick = (coordinates: LatLng, name: string) => () => {
    this.setState({
      locationFrom: { coordinates: coordinates, name: name }
    });

    this.reverseGeocodeCoordinates(GeocodeCoordinate.From, coordinates.lat.toString(), coordinates.lng.toString());
    this.exitSelectedFavouriteLocation();
  };

  /**
   * Turns selected favourite location to destination location
   *
   * @param coordinates coordinates string
   * @param name name string
   */
  private onSetDestinationClick = (coordinates: LatLng, name: string) => () => {
    this.setState({
      locationTo: { coordinates: coordinates, name: name }
    });

    this.reverseGeocodeCoordinates(GeocodeCoordinate.To, coordinates.lat.toString(), coordinates.lng.toString());
    this.exitSelectedFavouriteLocation();
  };

  /**
   * Handles Error message clearing
   */
  private onClearError = () => {
    this.setState({
      error: undefined
    });
  };

  /**
   * Hides selected favourite location marker
   */
  private exitSelectedFavouriteLocation = () => {
    this.setState({
      selectedFavouriteLocation: undefined,
      mapInteractive: true
    });
  };

  /**
   * Hides duplicate location waring
   */
  private exitDuplicateLocation = () => {
    this.setState({
      duplicateLocation: undefined
    });
  };

  /**
   * Switches map dragability
   */
  private switchMapInteraction = (mapInteractive: boolean) => () => this.setState({ mapInteractive: mapInteractive });

  /**
   * Checks is otp running low resources
   *
   * @returns is the otp is running low resources
   */
  static isOTPLowResources = () => {
    return process.env.REACT_APP_OTP_LOW_RESOURCES === "TRUE";
  };

}

/**
 * Redux mapper for mapping store state to component props
 *
 * @param state store state
 */
export function mapStateToProps(state: ReduxState) {
  return {
    accessToken: state.auth.accessToken,
    keycloak: state.auth.keycloak,
    displayedRoute: state.displayedRoute.displayedRoute,
    displayedFavouriteLocation: state.displayedFavouriteLocation.displayedFavouriteLocation
  };
}

/**
 * Redux mapper for mapping component dispatches
 *
 * @param dispatch dispatch method
 */
export function mapDispatchToProps(dispatch: Dispatch<ReduxActions>) {
  return {
    setDisplayedRoute: (displayedRoute?: Route) => dispatch(setDisplayedRoute(displayedRoute)),
    setDisplayedFavouriteLocation: (displayedFavouriteLocation?: FavouriteLocation) => dispatch(setDisplayedFavouriteLocation(displayedFavouriteLocation))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(MapScreen));